/**
 * Lecture et écriture des sessions, sous-groupes et affectations d'enfants
 * pour la gestion des groupes du personnel.
 *
 * Les droits sont ceux de la base : la coordination et l'administration
 * gèrent les sous-groupes (politiques de `subgroups`) et l'affectation des
 * inscriptions (`admin_update_registrations`). Rien ici ne touche à un
 * paiement ni à une facture.
 *
 * `registrations.subgroup_name` recopie le nom du groupe : les PDF et l'écran
 * des présences le lisent. Toute écriture qui change un nom ou une affectation
 * le tient donc à jour en même temps que `subgroup_id`.
 */
import { db } from '../payments/db';
import { sansJpa, stagesDesJpa } from '../jpa/stages.supabase';
import { estActive } from '../inscriptions/statut';
import { normaliserNom, rapprocherGroupes, type GroupePlanifie } from './repartition';
import type { SessionGroupe } from './sessions';

const TAILLE_DE_PAGE = 1000;
const IDS_PAR_REQUETE = 100;

const premier = <T,>(v: T | T[] | null): T | null => (Array.isArray(v) ? v[0] ?? null : v);

interface SessionLue {
  id: string;
  center_id: string;
  periode: string;
  semaine: string;
  start_date: string;
  end_date: string;
  current_registrations: number | null;
  stage: { title: string } | { title: string }[] | null;
  center: { name: string } | { name: string }[] | null;
}

/** Les sessions actives et non archivées, de toutes les périodes ; sans les journées pédagogiques (JPA13). */
export async function lireSessions(): Promise<SessionGroupe[]> {
  const sessions: SessionGroupe[] = [];
  const jpa = await stagesDesJpa();
  for (let depuis = 0; ; depuis += TAILLE_DE_PAGE) {
    const { data, error } = await sansJpa(db
      .from('sessions')
      .select('id, center_id, periode, semaine, start_date, end_date, current_registrations, stage:stages(title), center:centers(name)')
      .eq('active', true)
      .eq('archived', false)
      .order('start_date')
      .order('id')
      .range(depuis, depuis + TAILLE_DE_PAGE - 1), jpa);
    if (error) throw error;
    const lignes = (data ?? []) as unknown as SessionLue[];
    for (const s of lignes) {
      sessions.push({
        id: s.id,
        centreId: s.center_id,
        centre: premier(s.center)?.name ?? 'Centre inconnu',
        stage: premier(s.stage)?.title ?? 'Stage inconnu',
        periode: s.periode,
        semaine: s.semaine,
        debut: s.start_date,
        fin: s.end_date,
        inscrits: s.current_registrations ?? 0,
      });
    }
    if (lignes.length < TAILLE_DE_PAGE) break;
  }
  return sessions;
}

export interface SousGroupe {
  id: string;
  sessionId: string;
  nom: string;
  effectif: number;
}

interface SousGroupeLu {
  id: string;
  session_id: string;
  name: string;
  current_count: number | null;
}

/** Les sous-groupes des sessions données, dans l'ordre de leur création. */
export async function lireSousGroupes(sessionIds: string[]): Promise<SousGroupe[]> {
  const groupes: SousGroupe[] = [];
  for (let i = 0; i < sessionIds.length; i += IDS_PAR_REQUETE) {
    const { data, error } = await db
      .from('subgroups')
      .select('id, session_id, name, current_count')
      .in('session_id', sessionIds.slice(i, i + IDS_PAR_REQUETE))
      .order('created_at')
      .order('id');
    if (error) throw error;
    for (const g of (data ?? []) as SousGroupeLu[]) {
      groupes.push({ id: g.id, sessionId: g.session_id, nom: g.name, effectif: g.current_count ?? 0 });
    }
  }
  return groupes;
}

export interface InscriptionGroupe {
  id: string;
  sousGroupeId: string | null;
  nom: string;
  prenom: string;
  dateNaissance: string | null;
}

interface InscriptionLue {
  id: string;
  subgroup_id: string | null;
  cancellation_status: string | null;
  kid: { nom: string; prenom: string; date_naissance: string | null } | { nom: string; prenom: string; date_naissance: string | null }[] | null;
}

/**
 * Les enfants qui tiennent une place dans la session.
 *
 * Une annulation seulement demandée tient encore la place
 * (`inscriptions/statut`) : l'enfant reste à répartir.
 */
export async function lireInscriptionsDeSession(sessionId: string): Promise<InscriptionGroupe[]> {
  const inscriptions: InscriptionGroupe[] = [];
  for (let depuis = 0; ; depuis += TAILLE_DE_PAGE) {
    const { data, error } = await db
      .from('registrations')
      .select('id, subgroup_id, cancellation_status, kid:kids(nom, prenom, date_naissance)')
      .eq('activity_id', sessionId)
      .order('id')
      .range(depuis, depuis + TAILLE_DE_PAGE - 1);
    if (error) throw error;
    const lignes = (data ?? []) as unknown as InscriptionLue[];
    for (const r of lignes) {
      if (!estActive(r.cancellation_status)) continue;
      const kid = premier(r.kid);
      inscriptions.push({
        id: r.id,
        sousGroupeId: r.subgroup_id,
        nom: kid?.nom ?? '',
        prenom: kid?.prenom ?? '',
        dateNaissance: kid?.date_naissance ?? null,
      });
    }
    if (lignes.length < TAILLE_DE_PAGE) break;
  }
  return inscriptions.sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr'));
}

export async function creerSousGroupe(sessionId: string, nom: string, auteurId: string | null): Promise<SousGroupe> {
  const { data, error } = await db
    .from('subgroups')
    .insert({ session_id: sessionId, name: normaliserNom(nom), created_by: auteurId })
    .select('id, session_id, name, current_count')
    .single();
  if (error) throw error;
  const g = data as SousGroupeLu;
  return { id: g.id, sessionId: g.session_id, nom: g.name, effectif: g.current_count ?? 0 };
}

/** Renomme le groupe, et le nom recopié sur les inscriptions qui y sont. */
export async function renommerSousGroupe(id: string, nom: string): Promise<void> {
  const propre = normaliserNom(nom);
  const { error } = await db.from('subgroups').update({ name: propre }).eq('id', id);
  if (error) throw error;
  const { error: erreurInscriptions } = await db
    .from('registrations')
    .update({ subgroup_name: propre })
    .eq('subgroup_id', id);
  if (erreurInscriptions) throw erreurInscriptions;
}

/** Remet les enfants du groupe parmi les non-assignés, puis supprime le groupe. */
export async function supprimerSousGroupe(id: string): Promise<void> {
  const { error: erreurInscriptions } = await db
    .from('registrations')
    .update({ subgroup_id: null, subgroup_name: null })
    .eq('subgroup_id', id);
  if (erreurInscriptions) throw erreurInscriptions;
  const { error } = await db.from('subgroups').delete().eq('id', id);
  if (error) throw error;
}

/** Place des inscriptions dans un groupe, ou les en retire (`groupe` null). */
export async function affecterInscriptions(
  inscriptionIds: string[],
  groupe: { id: string; nom: string } | null,
): Promise<void> {
  for (let i = 0; i < inscriptionIds.length; i += IDS_PAR_REQUETE) {
    const { error } = await db
      .from('registrations')
      .update({ subgroup_id: groupe?.id ?? null, subgroup_name: groupe?.nom ?? null })
      .in('id', inscriptionIds.slice(i, i + IDS_PAR_REQUETE));
    if (error) throw error;
  }
}

/**
 * Applique une répartition : crée les groupes qui manquent, y place les
 * enfants, puis supprime les anciens groupes absents du plan.
 *
 * Ce n'est pas une transaction (plusieurs requêtes), mais c'est rejouable :
 * les groupes sont retrouvés par leur nom et chaque affectation écrase la
 * précédente. Après une coupure, relancer la même répartition donne le même
 * résultat, sans doublon.
 */
export async function appliquerRepartition(
  sessionId: string,
  plan: GroupePlanifie[],
  existants: SousGroupe[],
  auteurId: string | null,
): Promise<void> {
  const { reutilises, aSupprimer } = rapprocherGroupes(
    existants.map((g) => ({ id: g.id, nom: g.nom })),
    plan.map((g) => g.nom),
  );

  for (const groupe of plan) {
    let cible = reutilises.get(groupe.nom) ?? null;
    if (!cible) {
      const cree = await creerSousGroupe(sessionId, groupe.nom, auteurId);
      cible = { id: cree.id, nom: cree.nom };
    } else if (cible.nom !== groupe.nom) {
      await renommerSousGroupe(cible.id, groupe.nom);
      cible = { id: cible.id, nom: groupe.nom };
    }
    await affecterInscriptions(groupe.enfants.map((e) => e.inscriptionId), cible);
  }

  for (const ancien of aSupprimer) {
    await supprimerSousGroupe(ancien.id);
  }
}
